import {
  apiClient,
  apiFileUploadClient,
  v3Client,
  v3FileUploadClient,
} from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { ToastType } from '@components/Toast';
import { LTLCatalogs } from '@store/orders/types';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { Comments } from '@typeDefinitions/comments';
import { logError } from '@utils/errorLogging';
import { createError } from '@utils/errorUtils';
import { handlePopupToast, handleToast, printLog } from '@utils/utils';

import { setEdiConfigurationStore } from './store';
import {
  AddOrEditCommentPayload,
  AddProductDetails,
  CarrierRestrictionReasonCodeT,
  CarrierRestrictionsPageT,
  CarrierRestrictionStatusUpdateT,
  Comment,
  UpdateCreateCarrierRestrictionPayloadT,
  CreateEditRequestPayload,
  CreditRequestPayload,
  CustomerClaimHistoryItem,
  CustomerEditHistoryItem,
  CustomerGeneralDocuments,
  CustomerPaymentHistoryItem,
  DocumentDetails,
  ediConfigurationType,
  Group,
  ICreditRequest,
  ICustomerDetails,
  ICustomerUserNameExists,
  RemoveFromWalletPayloadT,
  UploadedAutoAcceptDeclineSheet,
} from './types';
const handleCustomerError = (error: unknown, toastMessage: string) => {
  if (Array.isArray(error)) {
    const errorMessages = error.map((v) => {
      if (typeof v === 'string') {
        return v;
      } else if (typeof v === 'object' && v !== null && 'errorMessage' in v) {
        return (v as { errorMessage: string }).errorMessage;
      }
    });
    handleToast(ToastType.Error, errorMessages.join(', '));
  } else {
    handleToast(ToastType.Error, toastMessage);
  }
};

export const fetchCustomerData = async (customerId: string) => {
  try {
    const response: V4ApiResponse<ICustomerDetails> = await apiClient.get(
      `Customer/getcustomerdata/${customerId}`,
    );
    if (response.isSuccess) {
      return response.value;
    }
  } catch (error: unknown) {
    handleCustomerError(error, 'Failed to fetch customer detail Data');
  }
};

export async function addCustomerGeneralComment(
  payload: AddOrEditCommentPayload,
) {
  try {
    const response: V4ApiResponse<Comments> = await apiClient.post(
      'Customer/Comment',
      payload,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.generalCommentsAdd,
      ]),
    );
    return response;
  } catch (e: unknown) {
    const error = createError(e, 'Adding carrier comments failed');
    handleToast(ToastType.Error, error.message);
  }
}

export async function deleteCustomerGeneralComment(commentId: number) {
  try {
    const response: V4ApiResponse<Comment> = await apiClient.delete(
      `Customer/Comment/${commentId}`,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.generalCommentsDelete,
      ]),
    );
    return response.isSuccess;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to delete comment');
    handleToast(ToastType.Error, e.message);
  }
}

export const getCustomerUserNameExists = async (
  payload: ICustomerUserNameExists,
) => {
  try {
    const response: V4ApiResponse<boolean> = await apiClient.get(
      'Customer/CustomerUserNameExists',
      {
        params: {
          username: payload.username,
          customerId: payload.customerId,
          customercontactId: payload.customercontactId,
        },
      },
    );
    return response;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to check username exists');
    handleToast(ToastType.Error, e.message);
  }
};

export const fetchCustomerDocumentType = async () => {
  try {
    const response: V4ApiResponse<DocumentDetails[]> = await apiClient.get(
      'customer/getdocumenttype',
    );
    return response;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to fetch customer document type');
    handleToast(ToastType.Error, e.message);
  }
};

export async function fetchCustomerEditHistory(id: string) {
  try {
    const response: V4ApiResponse<CustomerEditHistoryItem[]> =
      await apiClient.get(`customer/edithistory/${id}`);
    return response.value;
  } catch (error: unknown) {
    const e = createError(
      error,
      'Failed to fetch customer edit history. Please try again later',
    );
    handleToast(ToastType.Error, e.message);
    throw e;
  }
}

export async function getCustomerClaimsHistory(id: string) {
  try {
    const response: V4ApiResponse<CustomerClaimHistoryItem[]> =
      await apiClient.get(`/customer/claimhistory/${id}`);
    return response.value;
  } catch (error: unknown) {
    const e = createError(
      error,
      'Failed to fetch customer claim history. Please try again later.',
    );
    handleToast(ToastType.Error, e.message);
  }
}
export const fetchCustomerPaymentHistory = async (customerId: string) => {
  try {
    const response: V4ApiResponse<CustomerPaymentHistoryItem[]> =
      await apiClient.get(
        `Customer/CustomerPayments/${customerId}?Id=${customerId}`,
      );

    return response;
  } catch (error: unknown) {
    handleToast(ToastType.Error, 'Failed to fetch customer payment history');
    throw createError(error, 'Failed to fetch customer payment history');
  }
};

export const addProductDetails = async (payload: AddProductDetails) => {
  try {
    const response = await apiClient.post(
      'LtlCatalog',
      payload,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.productCatalogCreate,
      ]),
    );
    return response as unknown as AddProductDetails;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to Add Product Details');
    handleToast(ToastType.Error, e.message);
  }
};

export const editProductDetails = async (payload: AddProductDetails) => {
  try {
    const response = await apiClient.put(
      'LtlCatalog',
      payload,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.productCatalogUpdate,
      ]),
    );
    return response as unknown as AddProductDetails;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to Edit Product Details');
    handleToast(ToastType.Error, e.message);
  }
};

export const deleteProductDetails = async (id: string) => {
  try {
    const res: {
      isSuccess: boolean;
    } = await apiClient.delete(
      `LtlCatalog/${id}`,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.productCatalogDelete,
      ]),
    );
    if (Boolean(res.isSuccess)) {
      handleToast(ToastType.Success, 'Product Deleted Successfully');
      return res;
    }
    handleToast(ToastType.Error, 'Failed to delete product');
  } catch (error: unknown) {
    throw createError(error, 'Failed to Delete Product Details');
  }
};

export const fetchProductById = async (id: string) => {
  try {
    const response = await apiClient.get(`LtlCatalog/${id}`);
    return response as unknown as AddProductDetails;
  } catch (error: unknown) {
    handleToast(ToastType.Error, 'Failed to fetch product by id');
    throw createError(error, 'Failed to fetch product by id');
  }
};

export async function fetchCapacityGroups() {
  try {
    const response: V4ApiResponse<Group[]> = await apiClient.get(
      'group/capacitygroups',
    );
    return response;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to fetch customer capacity groups');
    handleToast(ToastType.Error, e.message);
  }
}

export const fetchEdiConfiguration = async (customerId: string) => {
  try {
    const response: V4ApiResponse<ediConfigurationType> = await apiClient.get(
      `Edi/GetEDIConfigurationsForCustomer/${customerId}`,
    );
    return response;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to fetch edi configuration');
    handleToast(ToastType.Error, e.message);
  }
};

export const fetchEdiProviders = async () => {
  try {
    const response: V4ApiResponse<string[]> = await apiClient.get(
      'EdiConfig/getproviders',
    );
    printLog('EDI Providers response', response);
    return response;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to fetch edi providers');
    handleToast(ToastType.Error, e.message);
  }
};

export const sendEdiConfigurationData = async (
  data: unknown,
  cb?: () => void,
) => {
  try {
    const response: V4ApiResponse<ediConfigurationType> = await apiClient.post(
      'Edi/saveediconfigurations',
      data,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.ediSaveConfiguration,
      ]),
    );
    return response;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to save edi configuration');
    handleToast(ToastType.Error, e.message);
  } finally {
    cb && cb();
  }
};
// this was never being used
export const uploadProductCatalog = async (id: string, file: File) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await v3FileUploadClient.post(
      `LTLCatalog/Import/${id}`,
      formData,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.productCatalogImport,
      ]),
    );
    return response as unknown as {
      success: boolean;
      message: string;
      data: {
        validEntities: LTLCatalogs[];
      };
    };
  } catch (error: unknown) {
    const e = createError(error, 'Failed to upload product catalog');
    handleToast(ToastType.Error, e.message);
  }
};

export const uploadQuoteFile = async (id: string, file: File) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await v3FileUploadClient.post(
      `Quote/Import/${id}`,
      formData,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.quotesImport,
      ]),
    );
    const resp = response as unknown as {
      success: boolean;
      message: string;
      data: {
        imported: string[];
        notImported: {
          name: string;
          reason: string;
        }[];
      };
    };
    if (resp.data.imported.length > 0) {
      handleToast(ToastType.Success, 'Quote file uploaded successfully');
      return true;
    } else if (resp.data.notImported.length > 0) {
      handleToast(ToastType.Error, resp.data.notImported[0].reason);
      return false;
    }
  } catch (error: unknown) {
    throw createError(error, 'Failed to upload quote file');
  }
};

export const deleteCollectionCommentsCall = async (commentId: number) => {
  try {
    const response: V4ApiResponse<Comment> = await apiClient.delete(
      `Customer/Comment/${commentId}`,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.collectionsDeleteComment,
      ]),
    );
    if ('isSuccess' in response) {
      handleToast(ToastType.Success, 'Collection comment deleted successfully');
      return true;
    }
  } catch (error: unknown) {
    handleToast(ToastType.Error, 'Failed to delete collection comment');
  }
};

export const addCollectionCommentCall = async (
  customerId: number,
  comment: string,
) => {
  try {
    const resp = await apiClient.post(
      'Customer/Comment',
      {
        comment,
        objectId: customerId,
        type: 'Collections',
      },
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.collectionsAddComment,
      ]),
    );
    if ('isSuccess' in resp && 'value' in resp) {
      handleToast(ToastType.Success, 'Collection comment added successfully');
      return resp.value;
    }
  } catch (error: unknown) {
    handleToast(ToastType.Error, 'Failed to add collection comment');
  }
};

export const editCollectionComment = async (payload: {
  id: number;
  comment: string;
  objectId: number;
  type: string;
}) => {
  try {
    const resp: V4ApiResponse<Comment[]> = await apiClient.put(
      'Customer/Comment',
      payload,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.collectionsEditComment,
      ]),
    );
    return resp;
  } catch (error: unknown) {
    handleCustomerError(error, 'Failed to edit collection comment');
  }
};

export const creditIncreaseCall = async (payload: ICreditRequest) => {
  try {
    const res: V4ApiResponse<null> = await apiClient.post(
      'Request/CreditIncrease',
      payload,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.headerCreditRequestSubmit,
      ]),
    );
    return res;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to increase credit');
    handleToast(ToastType.Error, e.message);
  }
};

export const updateCustomerDetailsCall = async (
  payload: ICustomerDetails,
  changeTrackingSources: ChangeTrackingSource[] = [],
) => {
  try {
    const res: V4ApiResponse<ICustomerDetails> = await apiClient.put(
      'Customer',
      payload,
      buildChangeTrackingRequestConfig(changeTrackingSources),
    );

    if (Boolean(res.isSuccess) && Boolean(res.value)) {
      handlePopupToast(ToastType.Success, 'Customer changes saved');
      return res.value;
    }
  } catch (error: unknown) {
    handleCustomerError(error, 'Failed to update customer details');
  }
};

export const createEdiRequestCall = async (
  payload: CreateEditRequestPayload,
) => {
  try {
    const response: { success: boolean } = await v3Client.post(
      'Request/EDISetup',
      payload,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.ediRequestSetup,
      ]),
    );
    return response;
  } catch (error: unknown) {
    throw createError(error, 'Failed to create edi request');
  }
};

export const customerCreditRequest = async (payload: CreditRequestPayload) => {
  try {
    const response: V4ApiResponse<null> = await apiClient.post(
      'Request/creditrequestresponse',
      payload,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.headerCreditRequestSubmit,
      ]),
    );
    return response;
  } catch (error: unknown) {
    const e = createError(error, 'Request Resolved failed manually');
    handleToast(ToastType.Error, e.message);
  }
};

export const fetchCustomerDetailsDocuments = async (customerId: string) => {
  try {
    const response: V4ApiResponse<CustomerGeneralDocuments[]> =
      await apiClient.get(`customer/getdocument/${customerId}`);
    return response;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to fetch customer document data');
    handleToast(ToastType.Error, e.message);
  }
};

export const uploadCustomerDetailsDocument = async (
  customerId: string,
  file: File,
  type: 'Public' | 'Confidential' | 'Misc' | 'Logo',
  changeTrackingSource: ChangeTrackingSource = CHANGE_TRACKING_SOURCES
    .customerDetails.generalUploadDocumentsUpload,
) => {
  try {
    const formData = new FormData();
    formData.append('Files', file);
    formData.append('DocumentEntityType', '5');
    formData.append('EntityId', customerId.toString());
    formData.append('Folder', type);
    const res: V4ApiResponse<{
      results: { documentId: number; name: string; url: string }[];
    }> = await apiClient.post('customer/uploaddocument', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...buildChangeTrackingRequestConfig([changeTrackingSource])?.headers,
      },
    });
    return res.value;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to upload customer document');
    handleToast(ToastType.Error, e.message);
  }
};

export const deleteCustomerDetailsDocument = async (payload: {
  file: string;
  type: string;
}) => {
  try {
    const response: V4ApiResponse<null> = await apiClient.delete(
      'customer/deletedocument',
      {
        data: payload,
        ...buildChangeTrackingRequestConfig([
          CHANGE_TRACKING_SOURCES.customerDetails.generalUploadDocumentsDelete,
        ]),
      },
    );
    return response.isSuccess;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to delete customer document');
    handleToast(ToastType.Error, e.message);
  }
};

export const updateCustomerDefaultLogoUrl = async (payload: {
  defaultLogoUrl: string | null;
  id: number;
}) => {
  try {
    const response: V4ApiResponse<null> = await apiClient.put(
      `Customer/UpdateDefaultLogoUrl/${payload.id}`,
      payload,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails
          .generalUploadDocumentsSetDefaultLogo,
      ]),
    );
    return response;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to update customer default logo url');
    handleToast(ToastType.Error, e.message);
  }
};

export const downloadCustomerAugmentContactCard = async (
  customerId: number,
  displayName?: string,
) => {
  try {
    const response = await apiClient.get(`Customer/${customerId}/contactcard`, {
      params: displayName ? { displayName } : undefined,
      responseType: 'blob',
    });

    const blob =
      (response as unknown as { data?: Blob }).data ??
      (response as unknown as Blob);

    if (!(blob instanceof Blob) || blob.size === 0) {
      handleToast(ToastType.Error, 'Failed to download Augment contact card');
      return false;
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `augment-contact-${customerId}.vcf`;
    link.click();
    URL.revokeObjectURL(url);
    return true;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to download Augment contact card');
    handleToast(ToastType.Error, e.message);
    return false;
  }
};

export async function fetchCustomerOfficeGroupsCall(
  officeId: number,
  includeAll?: boolean,
) {
  try {
    const response: V4ApiResponse<Group[]> = await apiClient.get(
      `Office/Groups/${officeId}?Id=${officeId}${includeAll ? '&includeAll=true' : ''}`,
    );
    return response;
  } catch (error: unknown) {
    const e = createError(error, 'There was a issue fetching office groups.');
    handleToast(ToastType.Error, e.message);
  }
}

export const updateCustomerComment = async (
  payload: Record<string, string | number | null>,
) => {
  try {
    const resp: V4ApiResponse<Comment[]> = await apiClient.put(
      'Customer/Comment',
      payload,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.generalCommentsEdit,
      ]),
    );
    return resp.isSuccess;
  } catch (error: unknown) {
    const e = createError(error, 'Updating customer comments failed');
    handleToast(ToastType.Error, e.message);
  }
};

export const fetchEDIConfigIsTenderAutoResponseAuto990Decline = async (
  customerId: string,
) => {
  try {
    const response: V4ApiResponse<boolean> = await apiClient.get(
      `FeatureFlag/IsTenderAuto990AcceptDecline/${customerId}`,
    );
    return response;
  } catch (error: unknown) {
    const e = createError(
      error,
      'Failed to fetch customer tender auto response data',
    );
    handleToast(ToastType.Error, e.message);
  }
};

export const fetchUploadedAutoAcceptDeclineSheet = async (
  customerId: string,
) => {
  try {
    const response: V4ApiResponse<UploadedAutoAcceptDeclineSheet> =
      await apiClient.get(
        `EDI/GetUploadedAutoAcceptDeclineSheet/${customerId}`,
      );
    return response;
  } catch (error: unknown) {
    const e = createError(
      error,
      'Failed to fetch uploaded auto accept decline sheet',
    );
    handleToast(ToastType.Error, e.message);
  }
};

const handleError = (error: unknown, toastMessage: string) => {
  if (Array.isArray(error)) {
    const errorMessages = error.map((v) => {
      if (typeof v === 'string') {
        return v;
      } else if (typeof v === 'object' && v !== null && 'errorMessage' in v) {
        return (v as { errorMessage: string }).errorMessage;
      }
      return 'Unspecified/unknown error';
    });
    setEdiConfigurationStore('errorMessage', errorMessages.join(', '));
  } else {
    throw new Error(toastMessage);
  }
  handleToast(ToastType.Error, toastMessage);
};

export const uploadAutoAcceptDeclineSheet = async (
  customerId: string,
  file: File,
) => {
  try {
    const formData = new FormData();
    formData.append('formFile', file);
    const response: V4ApiResponse<null> = await apiFileUploadClient.post(
      `EDI/UploadAutoAcceptDeclineSheet/${customerId}`,
      formData,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.ediUploadAutoAcceptDecline,
      ]),
    );
    return response;
  } catch (error: unknown) {
    handleError(error, 'Failed to upload auto accept decline sheet');
  }
};

export const toggleAutoAcceptDecline = async (
  customerId: string,
  enabled: boolean,
) => {
  try {
    const response: V4ApiResponse<null> = await apiClient.post(
      'edi/ToggleAutoAcceptDecline',
      {
        customerId,
        enabled,
      },
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.ediToggleAutoAcceptDecline,
      ]),
    );
    return response;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to toggle auto accept decline');
    handleToast(ToastType.Error, e.message);
  }
};

export const removeFromWalletApi = async (
  payload: RemoveFromWalletPayloadT,
) => {
  try {
    const response: V4ApiResponse<null> = await apiClient.delete(
      'DormantCustomer',
      {
        data: payload,
        ...buildChangeTrackingRequestConfig([
          CHANGE_TRACKING_SOURCES.customerDetails
            .generalOwnershipRemoveFromWallet,
        ]),
      },
    );
    return response.isSuccess;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to remove customer from wallet');
    handleToast(ToastType.Error, e.message);
  }
};

export const fetchCarrierRestrictionReasonCodes = async () => {
  try {
    const response: V4ApiResponse<CarrierRestrictionReasonCodeT[]> =
      await apiClient.get('carrier-restriction-reason-codes');
    if (response.isSuccess) {
      return response.value;
    }
    throw response.errors;
  } catch (error: unknown) {
    logError('fetchCarrierRestrictionReasonCodes', error as Error);
    throw createError(
      error,
      'Failed to fetch carrier restriction reason codes',
    );
  }
};

export const createCarrierRestriction = async (
  customerId: string | number,
  payload: UpdateCreateCarrierRestrictionPayloadT,
) => {
  try {
    const response: V4ApiResponse<null> = await apiClient.post(
      `Customer/${customerId}/carrier-restrictions`,
      payload,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.carrierRestrictionCreate,
      ]),
    );
    return response;
  } catch (error: unknown) {
    logError('createCarrierRestriction', error as Error);
    throw createError(error, 'Failed to create carrier restriction');
  }
};

export const fetchCarrierRestrictions = async (
  customerId: string | number,
  pageNumber = 1,
  pageSize = 100,
) => {
  try {
    const response: V4ApiResponse<CarrierRestrictionsPageT> =
      await apiClient.get(`Customer/${customerId}/carrier-restrictions`, {
        params: { pageNumber, pageSize },
      });
    if (!response.isSuccess) {
      throw response.errors;
    }
    return response.value;
  } catch (error: unknown) {
    logError('fetchCarrierRestrictions', error as Error);
    throw createError(error, 'Failed to fetch carrier restrictions');
  }
};

export const deleteCarrierRestriction = async (
  customerId: string | number,
  restrictionId: number,
) => {
  try {
    const response: V4ApiResponse<null> = await apiClient.delete(
      `Customer/${customerId}/carrier-restrictions/${restrictionId}`,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.carrierRestrictionDelete,
      ]),
    );
    return response;
  } catch (error: unknown) {
    logError('deleteCarrierRestriction', error as Error);
    throw createError(error, 'Failed to delete carrier restriction');
  }
};

export const updateCarrierRestrictionStatus = async (
  customerId: string | number,
  restrictionId: number,
  isActive: boolean,
) => {
  try {
    const response: V4ApiResponse<CarrierRestrictionStatusUpdateT> =
      await apiClient.put(
        `Customer/${customerId}/carrier-restrictions/${restrictionId}/status`,
        { isActive },
        buildChangeTrackingRequestConfig([
          CHANGE_TRACKING_SOURCES.customerDetails
            .carrierRestrictionStatusUpdate,
        ]),
      );
    return response;
  } catch (error: unknown) {
    logError('updateCarrierRestrictionStatus', error as Error);
    throw createError(error, 'Failed to update carrier restriction status');
  }
};

export const updateCarrierRestriction = async (
  customerId: string | number,
  restrictionId: number,
  payload: UpdateCreateCarrierRestrictionPayloadT & { isActive: boolean },
) => {
  try {
    const response: V4ApiResponse<CarrierRestrictionStatusUpdateT> =
      await apiClient.put(
        `Customer/${customerId}/carrier-restrictions/${restrictionId}`,
        payload,
        buildChangeTrackingRequestConfig([
          CHANGE_TRACKING_SOURCES.customerDetails.carrierRestrictionUpdate,
        ]),
      );
    return response;
  } catch (error: unknown) {
    logError('updateCarrierRestriction', error as Error);
    throw createError(error, 'Failed to update carrier restriction');
  }
};
